<template>
  <div class="flex-container">
    <div v-show="!isShowBulkDealManagement" class="main-area">
      <img
        class="product-deal-banner"
        alt="product-deal-banner"
        src="https://n11scdn.akamaized.net/a1/org/26/06/03/91/20/75/60/39/68/38/99/73/32466404060161265160.png"
      />
      <div class="title">
        Ürün Komisyon Teklifleri
      </div>
      <div class="filter-area-bottom">
        <ProductDealFilters
          ref="filterComponentRef"
          @filter-emitter="changeFilterPayload"
        />
      </div>
      <div class="list-area box">
        <TableForDeal
          :data="data"
          :columns="columns"
          :is-loading="isLoading"
          :page-count="pageCount"
          :pagination="getPaginationForTable()"
          @onPageChanged="changePaginationPayload"
          @go-bulk-deal-management="isShowBulkDealManagement = true"
          @onFilterChanged="changeFilterPayload"
        />
      </div>
    </div>
    <BulkDealManagement
      v-show="isShowBulkDealManagement"
      @go-deal-main-page="handleGoDealMainPage"
      :start-date="payload.startDate"
      :finish-date="payload.finishDate"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { sendPageViewEvent } from '@/utils/ga4/helper';
import ProductDealFilters from './partials/ProductDealFilters';
import TableForDeal from './partials/TableForDeal';
import {
  columns,
  getTableDataAsync,
  addAppliedDealToTableData,
  buildPendingSpecialCommissionRow,
} from './services/productDealService';
import BulkDealManagement from './BulkDealManagement/BulkDealManagement.vue';

const SPECIAL_COMMISSION_HOME_SOURCE = 'specialCommissionDashboard';

const defaultPayload = {
  startDate: new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 19).replace('T', ' '),
  finishDate: '2099-01-04 23:59:59',
  productName: '',
  productCode: '',
  stockCode: '',
  gtin: '',
  status: null,
  searchCategoryIds: [],
  isCustomDeal: null,
  page: 0,
  pageSize: 10,
};

const store = useStore();

const sellerInfo = computed(() => ({
  sellerId: store.state.auth.user.sellerId || '',
  sellerName: store.state.auth.user.username || '',
}));
const isLoading = ref(true);
const data = ref([]);
const pageCount = ref(0);
const payload = ref({ ...defaultPayload });
const isShowBulkDealManagement = ref(false);

// Reset responsive features until the responsive design is ready.
const resetResponsive = () => {
  document.getElementById('app').style.minWidth = '1320px';
  document.querySelector('#app .topMenu').style.cssText =
    'display:flex !important';
  document.querySelector('#app .navbar').style.cssText =
    'display:block !important;margin-top:unset;';
};

const sendRequestAsync = async () => {
  isLoading.value = true;
  try {
    const response = await getTableDataAsync(payload.value);
    if (response.error) return;
    data.value = response.data;
    pageCount.value = response.pageCount;
  } catch (error) {
    throw error;
  } finally {
    isLoading.value = false;
  }
};

const changeFilterPayload = (newFiltersPayload) => {
  if(!newFiltersPayload.hasOwnProperty("gtin")) {
    delete payload.value.isCustomDeal;
  }

  payload.value = {
    ...payload.value,
    ...newFiltersPayload,
    page: 0,
  };
};

const changePaginationPayload = (pagination) => {
  const { page, pageSize } = payload.value;
  if (page === pagination.page && pageSize === pagination.pageSize && !pagination.refreshKey) return;
  payload.value = {
    ...payload.value,
    ...pagination,
  };
};

const getPaginationForTable = () => {
  return {
    page: payload.value.page,
    pageSize: payload.value.pageSize,
  };
};

const injectPendingSpecialCommissionDeal = () => {
  const pending = store.state.productDeal.pendingDealOfferAction;
  if (
    !pending ||
    pending.source !== SPECIAL_COMMISSION_HOME_SOURCE ||
    !pending.dealDetails
  ) {
    return;
  }

  const alreadyListed = data.value.some(
    (row) => row.dealDto?.dealId === pending.dealId,
  );
  if (alreadyListed) return;

  const pendingRow = buildPendingSpecialCommissionRow(pending.dealDetails);
  if (pendingRow) data.value = [pendingRow, ...data.value];
};
const handleGoDealMainPage = () => {
  isShowBulkDealManagement.value = false;
  sendRequestAsync();
}

onMounted(async () => {
  resetResponsive();
  await sendRequestAsync();
  injectPendingSpecialCommissionDeal();
  store.dispatch('common/clearCustomDealNotification');
  sendPageViewEvent({
    page_type: 'Ürün Komisyon Teklifleri',
    seller_name: sellerInfo.value.sellerName,
    seller_id: sellerInfo.value.sellerId,
  });
});

watch(payload, () => sendRequestAsync());

watch(
  () => store.state.productDeal.appliedDealRowUpdate,
  (update) => {
    if (!update) return;
    data.value = addAppliedDealToTableData(data.value, update);
    store.commit('productDeal/CLEAR_APPLIED_DEAL_ROW_UPDATE');
    store.commit('productDeal/CLEAR_PENDING_DEAL_OFFER_ACTION');
  },
);
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';
@import '@/assets/styles/utilities';

.flex-container {
  display: flex;
  width: 100%;

  .main-area {
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: center;
  }

  .product-deal-banner {
    width: 100%;
    height: 100%;
    object-fit: cover;
    margin: 24px 0 16px;
  }

  .title {
    padding: 16px 0;
    color: $colorN80;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.2px;
    line-height: 26px;
  }

  .filter-area-bottom {
    width: 100%;
    padding: 24px;
    margin-bottom: 16px;
    background: $colorN00;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }

  .list-area {
    border-radius: 8px;

    &.box {
      width: 100%;
      padding: 24px;
      background: $colorN00;
    }
  }
}
</style>
